/**
 * Journal Statistics Component
 * Displays trade log-based statistics for the Journal page
 */

import React, { useMemo } from "react";
import {
  Box,
  SimpleGrid,
  useColorModeValue,
  Text,
} from "@chakra-ui/react";
import { Trade } from "../../../../types/trade";
import { calculateJournalStatistics, JournalStatistics as JournalStatisticsType } from "../../../../utils/journalStatistics";
import TradeLogMiniStatistics from "../../TradeLog/components/TradeLogMiniStatistics";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";

import { JournalEntry } from "../../../../types/journal";

interface JournalStatisticsProps {
  trades: Trade[];
  journalEntries?: JournalEntry[];
}

const JournalStatistics: React.FC<JournalStatisticsProps> = ({ trades, journalEntries = [] }) => {
  const { formatMoney } = useDisplayCurrency();
  const textColor = useColorModeValue("#575757", "white");
  const secondaryTextColor = useColorModeValue("gray.400", "gray.400");

  const statistics = useMemo(() => {
    return calculateJournalStatistics(trades, journalEntries);
  }, [trades, journalEntries]);

  // Get emotion color gradient
  const getEmotionGradient = (emotion: string): string => {
    const positiveEmotions = ["calm", "confident"];
    const negativeEmotions = ["anxious", "fearful"];
    
    if (positiveEmotions.includes(emotion.toLowerCase())) {
      return "linear-gradient(135deg, #06E363, #AAFAB1)";
    } else if (negativeEmotions.includes(emotion.toLowerCase())) {
      return "linear-gradient(135deg, #E00101, #FE4244)";
    }
    return "linear-gradient(135deg, #38BBD1, #3EEAEC)";
  };

  // Check if we have enough data
  const hasData = trades.length > 0;

  if (!hasData) {
    return (
      <Box mb="24px" p={6} bg={useColorModeValue("white", "#151517")} borderRadius="md" border="1px solid" borderColor={useColorModeValue("gray.200", "gray.600")}>
        <Text fontSize="lg" fontWeight="medium" color={textColor} mb={2} fontFamily="'Urbanist', sans-serif">
          Trading Psychology Insights
        </Text>
        <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
          Start logging trades with journaling fields to see insights about your trading psychology, discipline, and performance patterns.
        </Text>
      </Box>
    );
  }

  return (
    <Box mb="24px">
      <Text
        fontSize={{ base: "xl", md: "2xl" }}
        fontWeight="normal"
        color="#38BBD1"
        mb="16px"
        fontFamily="'Urbanist', sans-serif"
      >
        Trading Psychology Insights
      </Text>
      
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 5 }} spacing="24px">
        {/* 1. Average Pre-Trade Emotion */}
        {statistics.averagePreTradeEmotion.distribution.length > 0 && statistics.averagePreTradeEmotion.mostCommon !== "N/A" ? (
          <TradeLogMiniStatistics
            title="Average Pre-Trade Emotion"
            amount={statistics.averagePreTradeEmotion.mostCommon.charAt(0).toUpperCase() + statistics.averagePreTradeEmotion.mostCommon.slice(1)}
            percentage={Math.round(statistics.averagePreTradeEmotion.percentage)}
            backgroundGradient={getEmotionGradient(statistics.averagePreTradeEmotion.mostCommon)}
            tooltip={`Most common emotional state before entering trades. ${statistics.averagePreTradeEmotion.percentage.toFixed(2)}% of trades started with this emotion.`}
          />
        ) : (
          <TradeLogMiniStatistics
            title="Average Pre-Trade Emotion"
            amount="N/A"
            percentage={0}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            tooltip="Add pre-trade emotion to your trades in Trade Log to see your emotional patterns."
          />
        )}

        {/* 2. Plan Adherence Rate */}
        {statistics.planAdherenceRate.total > 0 ? (
          <TradeLogMiniStatistics
            title="Plan Adherence Rate"
            amount={`${statistics.planAdherenceRate.rate.toFixed(2)}%`}
            percentage={Math.round(statistics.planAdherenceRate.rate)}
            backgroundGradient={
              statistics.planAdherenceRate.rate >= 70
                ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                : statistics.planAdherenceRate.rate >= 50
                ? "linear-gradient(135deg, #F45B0B, #F49113)"
                : "linear-gradient(135deg, #E00101, #FE4244)"
            }
            tooltip={`${statistics.planAdherenceRate.followed} of ${statistics.planAdherenceRate.total} trades followed your plan. Higher adherence typically leads to better results.`}
          />
        ) : (
          <TradeLogMiniStatistics
            title="Plan Adherence Rate"
            amount="N/A"
            percentage={0}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            tooltip="Track whether you followed your plan in Trade Log to see your discipline metrics."
          />
        )}

        {/* 3. Average Confidence Level */}
        {statistics.averageConfidence.total > 0 ? (
          <TradeLogMiniStatistics
            title="Average Confidence Level"
            amount={`${statistics.averageConfidence.average.toFixed(2)}/10`}
            percentage={Math.round((statistics.averageConfidence.average / 10) * 100)}
            backgroundGradient={
              statistics.averageConfidence.average >= 8
                ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                : statistics.averageConfidence.average >= 5
                ? "linear-gradient(135deg, #38BBD1, #3EEAEC)"
                : "linear-gradient(135deg, #E00101, #FE4244)"
            }
            tooltip={`Average confidence level (1-10) before entering trades. Based on ${statistics.averageConfidence.total} trades.`}
          />
        ) : (
          <TradeLogMiniStatistics
            title="Average Confidence Level"
            amount="N/A"
            percentage={0}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            tooltip="Add confidence level (1-10) to your trades in Trade Log to track your confidence patterns."
          />
        )}

        {/* 4. Emotional Trading Frequency */}
        {statistics.emotionalTradingFrequency.total > 0 ? (
          <TradeLogMiniStatistics
            title="Emotional Trading"
            amount={`${statistics.emotionalTradingFrequency.percentage.toFixed(2)}%`}
            percentage={Math.round(statistics.emotionalTradingFrequency.percentage)}
            backgroundGradient={
              statistics.emotionalTradingFrequency.percentage < 20
                ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                : statistics.emotionalTradingFrequency.percentage < 40
                ? "linear-gradient(135deg, #F45B0B, #F49113)"
                : "linear-gradient(135deg, #E00101, #FE4244)"
            }
            tooltip={`${statistics.emotionalTradingFrequency.emotional} of ${statistics.emotionalTradingFrequency.total} trades involved emotions in decision-making. Lower is better for disciplined trading.`}
          />
        ) : (
          <TradeLogMiniStatistics
            title="Emotional Trading"
            amount="N/A"
            percentage={0}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            tooltip="Track whether emotions were involved in your trades to see your discipline level."
          />
        )}

        {/* 5. Best Performing Emotion */}
        {statistics.bestPerformingEmotion.totalTrades > 0 && statistics.bestPerformingEmotion.emotion !== "N/A" ? (
          <TradeLogMiniStatistics
            title="Best Performing Emotion"
            amount={statistics.bestPerformingEmotion.emotion.charAt(0).toUpperCase() + statistics.bestPerformingEmotion.emotion.slice(1)}
            percentage={statistics.bestPerformingEmotion.averagePnL >= 0 ? 100 : -100}
            backgroundGradient={
              statistics.bestPerformingEmotion.averagePnL >= 0
                ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                : "linear-gradient(135deg, #E00101, #FE4244)"
            }
            tooltip={`Trades with "${statistics.bestPerformingEmotion.emotion}" emotion averaged ${formatMoney(statistics.bestPerformingEmotion.averagePnL)} P&L across ${statistics.bestPerformingEmotion.totalTrades} trades.`}
          />
        ) : (
          <TradeLogMiniStatistics
            title="Best Performing Emotion"
            amount="N/A"
            percentage={0}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            tooltip="Add pre-trade emotion to closed trades to see which emotions lead to better performance."
          />
        )}

        {/* 6. Plan Adherence Impact */}
        {statistics.planAdherenceImpact.followedCount > 0 && statistics.planAdherenceImpact.deviatedCount > 0 ? (
          <TradeLogMiniStatistics
            title="Plan Adherence Impact"
            amount={formatMoney(statistics.planAdherenceImpact.difference)}
            percentage={statistics.planAdherenceImpact.difference >= 0 ? 100 : -100}
            backgroundGradient={
              statistics.planAdherenceImpact.difference >= 0
                ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                : "linear-gradient(135deg, #E00101, #FE4244)"
            }
            tooltip={`Average P&L difference when following plan (${formatMoney(statistics.planAdherenceImpact.followedPlanAvgPnL)}) vs deviating (${formatMoney(statistics.planAdherenceImpact.deviatedAvgPnL)}).`}
          />
        ) : (
          <TradeLogMiniStatistics
            title="Plan Adherence Impact"
            amount="N/A"
            percentage={0}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            tooltip="Track plan adherence on closed trades to see the performance impact of following your plan."
          />
        )}

        {/* 7. Temptation Frequency */}
        {statistics.temptationFrequency.total > 0 ? (
          <TradeLogMiniStatistics
            title="Temptation Frequency"
            amount={`${statistics.temptationFrequency.percentage.toFixed(2)}%`}
            percentage={Math.round(statistics.temptationFrequency.percentage)}
            backgroundGradient="linear-gradient(135deg, #F45B0B, #F49113)"
            tooltip={`${statistics.temptationFrequency.tempted} of ${statistics.temptationFrequency.total} trades involved temptation to deviate (${statistics.temptationFrequency.earlyExit} early exits, ${statistics.temptationFrequency.addedPosition} added positions).`}
          />
        ) : (
          <TradeLogMiniStatistics
            title="Temptation Frequency"
            amount="N/A"
            percentage={0}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            tooltip="Track if you were tempted to exit early or add to positions to see your discipline patterns."
          />
        )}

        {/* 8. Post-Trade Emotion Trends */}
        {statistics.postTradeEmotionTrends.distribution.length > 0 && statistics.postTradeEmotionTrends.mostCommon !== "N/A" ? (
          <TradeLogMiniStatistics
            title="Post-Trade Emotion"
            amount={statistics.postTradeEmotionTrends.mostCommon.charAt(0).toUpperCase() + statistics.postTradeEmotionTrends.mostCommon.slice(1)}
            percentage={Math.round(statistics.postTradeEmotionTrends.percentage)}
            backgroundGradient={
              ["satisfied", "proud", "relieved"].includes(statistics.postTradeEmotionTrends.mostCommon.toLowerCase())
                ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                : ["frustrated", "regretful", "disappointed"].includes(statistics.postTradeEmotionTrends.mostCommon.toLowerCase())
                ? "linear-gradient(135deg, #E00101, #FE4244)"
                : "linear-gradient(135deg, #38BBD1, #3EEAEC)"
            }
            tooltip={`Most common emotional state after closing trades. ${statistics.postTradeEmotionTrends.percentage.toFixed(2)}% of trades ended with this emotion.`}
          />
        ) : (
          <TradeLogMiniStatistics
            title="Post-Trade Emotion"
            amount="N/A"
            percentage={0}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            tooltip="Add post-trade emotion to your closed trades to see your emotional patterns after trading."
          />
        )}

        {/* 9. Confidence vs Performance */}
        {(statistics.confidenceVsPerformance.highConfidenceAvgPnL !== 0 || statistics.confidenceVsPerformance.lowConfidenceAvgPnL !== 0) && 
         (statistics.averageConfidence.total > 0) ? (
          <TradeLogMiniStatistics
            title="Confidence vs Performance"
            amount={statistics.confidenceVsPerformance.correlation === "positive" ? "Positive" : statistics.confidenceVsPerformance.correlation === "negative" ? "Negative" : "None"}
            percentage={statistics.confidenceVsPerformance.correlation === "positive" ? 100 : statistics.confidenceVsPerformance.correlation === "negative" ? -100 : 0}
            backgroundGradient={
              statistics.confidenceVsPerformance.correlation === "positive"
                ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                : statistics.confidenceVsPerformance.correlation === "negative"
                ? "linear-gradient(135deg, #E00101, #FE4244)"
                : "linear-gradient(135deg, #38BBD1, #3EEAEC)"
            }
            tooltip={statistics.confidenceVsPerformance.message}
          />
        ) : (
          <TradeLogMiniStatistics
            title="Confidence vs Performance"
            amount="N/A"
            percentage={0}
            backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            tooltip="Add confidence levels to closed trades to see if higher confidence correlates with better performance."
          />
        )}

        {/* 10. Journal Completion Rate */}
        {statistics.journalCompletionRate.total > 0 && (
          <TradeLogMiniStatistics
            title="Journal Completion"
            amount={`${statistics.journalCompletionRate.rate.toFixed(2)}%`}
            percentage={Math.round(statistics.journalCompletionRate.rate)}
            backgroundGradient={
              statistics.journalCompletionRate.rate >= 70
                ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                : statistics.journalCompletionRate.rate >= 50
                ? "linear-gradient(135deg, #F45B0B, #F49113)"
                : "linear-gradient(135deg, #E00101, #FE4244)"
            }
            tooltip={`${statistics.journalCompletionRate.complete} of ${statistics.journalCompletionRate.total} trades have complete journal entries (3+ fields filled).`}
          />
        )}
      </SimpleGrid>
    </Box>
  );
};

export default JournalStatistics;

